
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { LeakStatus } from '@/api/types'
import { ListLeaks } from '@/api/leaks'
import { IsMobile } from '@/utils/check'

interface StatusOption {
  value: number
  label: string
}

interface CardData {
  id: string
  title: string
  filterParams: any
  todayCount: number
  yesterdayCount: number
  startDate: string
}

interface CountResponse {
  totalCount: number
  success: boolean
}

@Component({
  name: 'DayToDayFilter'
})
export default class extends Vue {
  @Prop({ default: false }) private initialConfigMode!: boolean
  @Prop({ default: null }) private savedCardData!: CardData | null
  @Prop({ default: true }) private showDateInfo!: boolean
  @Prop({ default: true }) private showConfigButton!: boolean
  @Prop({ default: false }) private parentIsMobile!: boolean
  @Prop({ default: '#303133' }) private titleColor!: string
  @Prop({ default: '#303133' }) private numberColor!: string
  @Prop({ default: false }) private isSelected!: boolean

  // 过滤参数
  private filterParams = {
    status: [LeakStatus.Unreported, LeakStatus.NeedAssignRepairDept],
    keywords: [''] as string[],
    addressKeywords: [''] as string[],
    createTimeStart: '',
    createTimeEnd: '',
    page: 1,
    pageSize: 10
  }

  // 组件状态
  private isConfigMode = true
  private get mobileMode() {
    return IsMobile()
  }
  private loading = false

  // 卡片数据
  private cardTitle = ''
  private startDate: string = this.getDefaultStartDate()
  private todayCount = 0
  private yesterdayCount = 0
  private cardId = ''

  created() {
    // 初始化组件
    this.isConfigMode = this.initialConfigMode

    // 如果有保存的卡片数据，则恢复
    if (this.savedCardData) {
      this.restoreCardData(this.savedCardData)
    } else {
      // 默认设置起始日期为本月第一天
      this.updateCreateTimeRange()
    }

    window.addEventListener('resize', this.handleResize)
  }

  mounted() {
    // 如果是卡片模式，加载数据
    if (!this.isConfigMode) {
      this.fetchCardData()
    }
  }

  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  }

  // 获取默认起始日期（当月第一天）
  private getDefaultStartDate(): string {
    const today = new Date()
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
  }

  // 状态选项
  get statusOptions(): StatusOption[] {
    return [
      { value: LeakStatus.Unreported, label: '未上报' },
      { value: LeakStatus.NeedAssignRepairDept, label: '待分配' },
      { value: LeakStatus.NeedAssignRepairUser, label: '待认领' },
      { value: LeakStatus.Repairing, label: '维修中' },
      { value: LeakStatus.Repaired, label: '完成维修' },
      { value: LeakStatus.NoNeedRepair, label: '无需维修' },
      { value: LeakStatus.CantRepair, label: '未能维修' }
    ]
  }

  // 监听起始日期变化
  @Watch('startDate')
  private onStartDateChange() {
    this.updateCreateTimeRange()
  }

  // 获取移动模式状态
  get isMobile() {
    return this.parentIsMobile || this.mobileMode
  }

  // 获取变化的样式类
  get changeClass(): string {
    const change = this.todayCount - this.yesterdayCount
    if (change > 0) return 'increase'
    if (change < 0) return 'decrease'
    return 'unchanged'
  }

  // 获取变化的图标样式类
  get changeIconClass(): string {
    const change = this.todayCount - this.yesterdayCount
    if (change > 0) return 'el-icon-top'
    if (change < 0) return 'el-icon-bottom'
    // return 'el-icon-minus'  // 0的情况显示水平线图标
    return ''
  }

  // 格式化变化数值为字符串
  get formattedChange(): string {
    const change = this.todayCount - this.yesterdayCount
    // 首先检查是否为0
    if (change === 0) {
      return '0'
    }
    // 然后检查是否大于0
    if (change > 0) {
      return `+${change}`
    }
    // 小于0的情况
    return `${change}`
  }

  // 日期范围文本
  get dateRangeText(): string {
    const startDate = this.startDate || this.getDefaultStartDate()
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    return `${startDate} 至 ${todayStr}`
  }

  // 禁用未来日期
  private disableFutureDates(time: Date): boolean {
    return time.getTime() > Date.now()
  }

  // 检测窗口大小变化
  private handleResize() {
  }

  // 更新创建时间范围（起始日期到今天）
  private updateCreateTimeRange() {
    if (!this.startDate) {
      this.startDate = this.getDefaultStartDate()
    }

    // 设置起始时间
    this.filterParams.createTimeStart = `${this.startDate}T00:00:00Z`

    // 设置截止时间为今天
    const today = new Date()
    this.filterParams.createTimeEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}T23:59:59Z`
  }

  // 重置过滤器
  private resetFilter() {
    this.filterParams = {
      status: [LeakStatus.Unreported], // 重置时选择第一个选项（未上报）
      keywords: [''] as string[],
      addressKeywords: [''] as string[],
      createTimeStart: '',
      createTimeEnd: '',
      page: 1,
      pageSize: 10
    }

    this.cardTitle = ''
    this.startDate = this.getDefaultStartDate()
    this.updateCreateTimeRange()

    this.$message({
      message: '条件已重置',
      type: 'success'
    })
  }

  // 切换到配置模式
  private switchToConfigMode(event: Event) {
    event.stopPropagation()
    this.isConfigMode = true
  }

  // 保存为卡片
  private async saveAsCard() {
    // 验证卡片标题
    if (!this.cardTitle) {
      this.$message({
        message: '请输入卡片标题',
        type: 'warning'
      })
      return
    }

    this.loading = true

    try {
      // 更新日期范围
      this.updateCreateTimeRange()

      // 获取今日和昨日的数据
      await this.fetchCardData()

      // 创建卡片数据对象
      const cardData: CardData = {
        id: this.cardId || new Date().getTime().toString(),
        title: this.cardTitle,
        filterParams: { ...this.filterParams },
        todayCount: this.todayCount,
        yesterdayCount: this.yesterdayCount,
        startDate: this.startDate
      }

      // 切换到卡片模式
      this.isConfigMode = false

      // 发出保存卡片事件
      this.$emit('save-card', cardData)

      this.$message({
        message: '卡片已保存',
        type: 'success'
      })
    } catch (error) {
      console.error('保存卡片失败:', error)
      this.$message({
        message: '保存卡片失败，请重试',
        type: 'error'
      })
    } finally {
      this.loading = false
    }
  }

  // 处理卡片点击
  private handleCardClick() {
    // 发出卡片点击事件，携带过滤参数和卡片ID
    this.$emit('card-click', {
      ...this.filterParams,
      cardId: this.cardId,
      cardTitle: this.cardTitle
    })
  }

  // 恢复卡片数据
  private restoreCardData(cardData: CardData) {
    this.cardId = cardData.id
    this.cardTitle = cardData.title
    this.filterParams = { ...cardData.filterParams }
    this.startDate = cardData.startDate
    this.todayCount = cardData.todayCount
    this.yesterdayCount = cardData.yesterdayCount

    // 更新截止时间为今天
    this.updateCreateTimeRange()
  }

  // 获取卡片数据（今日和昨日的统计数）
  private async fetchCardData() {
    try {
      // 更新日期范围
      this.updateCreateTimeRange()

      // 获取今日统计数据
      const { data: todayData } = await ListLeaks({
        ...this.filterParams
      })
      this.todayCount = todayData.total || 0

      // 获取昨日统计数据
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`

      const { data: yesterdayData } = await ListLeaks({
        ...this.filterParams,
        createTimeEnd: `${yesterdayStr}T23:59:59Z`
      })
      this.yesterdayCount = yesterdayData.total || 0

      return {
        todayCount: this.todayCount,
        yesterdayCount: this.yesterdayCount
      }
    } catch (error) {
      console.error('获取统计数据失败:', error)
      throw error
    }
  }

  // 调用API获取数据统计
  private async fetchCountData(params: any): Promise<CountResponse> {
    try {
      const { data } = await ListLeaks(params)
      return {
        totalCount: data.total || 0,
        success: true
      }
    } catch (error) {
      console.error('获取数据计数失败:', error)
      return {
        totalCount: 0,
        success: false
      }
    }
  }
}
